
//A tool that:
//Implements heuristically derived hyperlinks from text geometries in F2D drawings (and mouse hover support for hyperlinks)
//Taken from Fluent POC:
// https://git.autodesk.com/fluent/lmv-ui/blob/master/app/scripts/tools/mouse-tracker.js#L273
// https://git.autodesk.com/fluent/lmv-ui/blob/master/app/scripts/tools/mouse-tracker.js#L171


    'use strict';

    const av = Autodesk.Viewing,
        avp = Autodesk.Viewing.Private;

    function isLinkMapValid(map) {
        return map && Object.keys(map).length > 0;
    }

    function addRenderBox(vbb, f2d, stringIndex, modelScale) {
        const i4 = 4 * stringIndex;

        let miny = f2d.stringBoxes[i4 + 1];
        let maxy = f2d.stringBoxes[i4 + 3];
        const border = (maxy - miny) * 0.2;
        miny = miny - border;
        maxy = maxy + border;
        const minx = f2d.stringBoxes[i4] - border;
        const maxx = f2d.stringBoxes[i4 + 2] + border;

        const dbId = f2d.stringDbIds[stringIndex];
        const vbase = vbb.vcount;

        const linkFillColor = 0x77bfbb3f;
        vbb.addVertexPolytriangle(minx, miny, linkFillColor, dbId, 0, 0);
        vbb.addVertexPolytriangle(maxx, miny, linkFillColor, dbId, 0, 0);
        vbb.addVertexPolytriangle(maxx, maxy, linkFillColor, dbId, 0, 0);
        vbb.addVertexPolytriangle(minx, maxy, linkFillColor, dbId, 0, 0);

        vbb.addIndices([0,1,2,0,2,3], vbase);

        vbb.addSegment(minx, miny, maxx, miny, 0, -2 * modelScale, linkFillColor|0xff000000, dbId, 0, 0, 0);
        vbb.addSegment(maxx, miny, maxx, maxy, 0, -2 * modelScale, linkFillColor|0xff000000, dbId, 0, 0, 0);
        vbb.addSegment(maxx, maxy, minx, maxy, 0, -2 * modelScale, linkFillColor|0xff000000, dbId, 0, 0, 0);
        vbb.addSegment(minx, maxy, minx, miny, 0, -2 * modelScale, linkFillColor|0xff000000, dbId, 0, 0, 0);
    }

    export default class HyperlinkTracker extends av.ToolInterface {
        #viewer;
        #loadViewableCb;
        #hyperlinksAddedCb;
        #idRead = [0, 0];
        #linkMaps = {};
        #dbId = 0;
        #lastX;
        #lastY;
        #mouseMoved = false;
        #isDragging = false;

        constructor(viewer, rootBubbleNode, loadViewableCb, hyperlinksAddedCb) {
            super();
            this.names = ['hyperlink-tracker'];
            this.#viewer = viewer;
            this.#loadViewableCb = loadViewableCb;
            this.#hyperlinksAddedCb = hyperlinksAddedCb;
            viewer.toolController.registerTool(this);
        }

        update() {
            const viewer = this.#viewer;
            if (!viewer.model || !viewer.model.is2d())
                return;

            if (!this.#isDragging) {
                if (this.#mouseMoved) {
                    const vp = viewer.impl.clientToViewport(this.#lastX, this.#lastY);
                    this.#dbId = viewer.impl.renderer().idAtPixel(vp.x, vp.y, this.#idRead);
                    this.#mouseMoved = false;
                }
            } else {
                this.#dbId = 0;
            }
            return false;
        }

        #getCurrentModel() {
            const mq = this.#viewer.impl.modelQueue();
            const visibleModels = mq.getModels();

            return visibleModels.length === 1 ? visibleModels[0] : undefined;
        }

        #getCurrentLinkMap() {
            let linkMap = this.#linkMaps[this.#idRead[1]];

            //Fallback for when a single model is loaded and the Model ID buffer is not working
            if (!linkMap) {
                const cm = this.#getCurrentModel();
                if (cm) {
                    linkMap = this.#linkMaps[cm.id];
                }
            }

            return linkMap;
        }

        handleSingleClick() {
            const viewer = this.#viewer;
            if (!viewer.model || !viewer.model.is2d())
                return;

            const vp = viewer.impl.clientToViewport(this.#lastX, this.#lastY);
            this.#dbId = viewer.impl.renderer().idAtPixel(vp.x, vp.y, this.#idRead);

            const linkMap = this.#getCurrentLinkMap();

            const linkNode = isLinkMapValid(linkMap) ? linkMap[this.#dbId] : null;

            if (linkNode) {
                this.#loadViewableCb(linkNode, linkMap.numHyperlinks);
                return true;
            }

            return false;
        }

        handleButtonDown(event) {
            this.#isDragging = true;
            this.#lastX = event.canvasX;
            this.#lastY = event.canvasY;
            return false;
        }

        handleButtonUp() {
            this.#isDragging = false;
            return false;
        }

        handleMouseMove(event) {
            this.#lastX = event.canvasX;
            this.#lastY = event.canvasY;
            this.#mouseMoved = true;
            return false;
        }

        #addLinkBoxMesh(model, vbb) {
            const fragId = model.getFragmentList().vizmeshes.length;
            const mdata = {mesh: vbb.toMesh(), is2d: true, packId: '0', meshIndex: fragId};
            const geometry = avp.BufferGeometryUtils.meshToGeometry(mdata);

            const matman = this.#viewer.impl.matman();
            const materialId = matman.create2DMaterial(model, {});
            const m = this.#viewer.impl.setupMesh(model, geometry, materialId, model.getData().loadOptions.placementTransform);
            model.activateFragment(fragId, m);
            return fragId;
        }

        #removeLinkBoxMesh(model, fragId) {
            // get link box mesh
            let fragList = model.getFragmentList();
            let mesh = fragList.vizmeshes[fragId];

            // Dispose geometry. MaterialManager will take care of the material dispose on model unload.
            // We don't dispose material here because it may be shared with other fragments of the model.
            mesh.geometry.dispose();

            delete fragList.vizmeshes[fragId];
        }

        #initializeHyperlinks(model) {
            if (!model || !model.is2d()) {
                return;
            }

            // Check if this model has been initialized already
            let linkMap = this.#linkMaps[model.id];
            if (linkMap) {
                return;
            }

            // find all sheets of the same document
            const node = model.getDocumentNode();
            const root = node && node.getRootNode();
            const sheets = root && root.search({role: '2d', type: 'geometry'});
            if (!sheets || !sheets.length) {
                return;
            }

            linkMap = this.#linkMaps[model.id] = {};

            let currentSheetBasePath = '';
            if (model.myData) {
                currentSheetBasePath = model.myData.basePath;
            }

            const getSheetUrn = (sheet) => {
                const resourceType = 'resource';
                if (sheet.children) {
                    for (const child of sheet.children) {
                        if (child.type() === resourceType) {
                            const urn = child.urn();
                            if (urn) {
                                // We remove the primaryGraphics.f2d part from the urn
                                let elements = urn.split('/');
                                elements.pop();
                                return elements.join('/');
                            }
                        }
                    }
                }
                return null;
            };

            const sheetIds = sheets.map(function (s) {
                const sheetUrn = getSheetUrn(s);
                //exclude current sheet, i. e. no links to self
                if (currentSheetBasePath.indexOf(sheetUrn) === -1) {
                    const split = s.name().split(' ');
                    const sheetName = split[0];
                    if (sheetName.length > 2) {
                        return sheetName;
                    }
                }
                return '';
            });

            //while finding hyperlink strings, we will also
            //create a geometry buffer containing all their bboxes
            //so we can render with their IDs.
            linkMap.numHyperlinks = 0;
            const vbb = new avp.VertexBufferBuilder();
            const modelScale = 1.0 / (model.getData().loadOptions.modelScale || 1);
            const f2d = model.getData();
            for (let i=0; i<f2d.strings.length; i++) {

                const idx = sheetIds.indexOf(f2d.strings[i]);

                if (idx >= 0) {
                    linkMap[f2d.stringDbIds[i]] = sheets[idx];

                    addRenderBox(vbb, f2d, i, modelScale);
                    linkMap.numHyperlinks++;
                }
            }

            linkMap.boxFragId = this.#addLinkBoxMesh(model, vbb);

            if (this.#hyperlinksAddedCb) {
                this.#hyperlinksAddedCb(model.id, linkMap.numHyperlinks);
            }
        }

        #onGeomLoaded = (e) => {
            this.#initializeHyperlinks(e.model);
        };

        // Execute cb for all sheets that are (completely) in memory
        #forAllSheetsInMemory(cb) {
            let allModels = this.#viewer.getAllModels();
            for (let i=0; i<allModels.length; i++) {
                let model = allModels[i];
                if (model.is2d() && model.isLoadDone()) {
                    cb(model);
                }
            }
        }

        #removeHyperlinks() {

            // Keep #linkMaps consistent with models: We don't want to leak dead box meshes in the models.
            // Therefore, we remove them from all models in memory
            this.#forAllSheetsInMemory(model => {
                let linkMap = this.#linkMaps[model.id];
                if (linkMap) {
                    this.#removeLinkBoxMesh(model, linkMap.boxFragId);
                }
            });

            this.#linkMaps = {};

            // make sure that removed hyperlink quads are not visible anymore
            this.#viewer.impl.invalidate(true, true);
        }

        activate() {

            // Make sure that we don't forget to init hyperlinks if the geom-load event has already happened
            this.#forAllSheetsInMemory(model => {
                this.#initializeHyperlinks(model);
            });

            this.#viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this.#onGeomLoaded);

            // make sure that added hyperlink quads appear instantly
            this.#viewer.impl.invalidate(true, true);
        }

        deactivate() {
            this.#viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this.#onGeomLoaded);
            this.#removeHyperlinks();
        }

        // Show a hand cursor, if the cursor is close to a hyperlink
        getCursor() {
            const linkMap = this.#getCurrentLinkMap();

            if (this.#dbId < -1 && isLinkMapValid(linkMap) && linkMap[this.#dbId]) {
                return 'pointer';
            }

            return null;
        }
    }

    avp.usePrototypeToolMethods(HyperlinkTracker);

    const namespace = AutodeskNamespace('Autodesk.AEC');
    namespace.HyperlinkTracker = HyperlinkTracker;
